import React from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";

/**
 * AgencyFees — tenant-branded fee/pricing page.
 *
 * Content lives at `brand.fees` (intro, plans: [{name, price, price_note,
 * subtitle, area, description, features, you_manage, best_suited}]).
 */
export default function AgencyFees() {
  const { slug: urlSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const tenantSlug = urlSlug || brand.tenant_slug || "foundation";
  const fees = brand.fees;
  const isUs = market === "us";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={`Fees | ${biz.name || "our agency"}`}
        description={fees?.intro || `Selling fees and pricing for ${biz.name || "our agency"}.`}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline">Fees</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-primary mt-6 tracking-tight leading-[1.05] max-w-4xl">
            {isUs ? "Commission, clearly explained." : "Selling fees, clearly explained."}
          </h1>
          {fees?.intro && (
            <p className="text-lg text-primary/85 leading-relaxed mt-6 max-w-2xl">{fees.intro}</p>
          )}
        </section>

        {fees?.plans?.length > 0 && (
          <section className="border-t border-border bg-secondary/15">
            <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-8">
              {fees.plans.map((plan, i) => (
                <div key={i} className="bg-background border border-border rounded-sm p-8 flex flex-col">
                  <div className="font-serif-display text-2xl text-primary">{plan.name}</div>
                  <div className="mt-4">
                    <span className="text-3xl font-serif-display text-primary">{plan.price}</span>
                    {plan.price_note && <div className="text-xs text-muted-foreground mt-1">{plan.price_note}</div>}
                  </div>
                  {plan.subtitle && <p className="text-sm text-primary/80 mt-3">{plan.subtitle}</p>}
                  {plan.area && <div className="text-xs uppercase tracking-widest text-accent mt-3">{plan.area}</div>}
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{plan.description}</p>
                  )}
                  {plan.features?.length > 0 && (
                    <ul className="mt-6 space-y-2.5 flex-1">
                      {plan.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-primary">
                          <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {plan.you_manage?.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-border">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">You manage</div>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        {plan.you_manage.map((m, j) => <li key={j}>{m}</li>)}
                      </ul>
                    </div>
                  )}
                  {plan.best_suited && (
                    <p className="text-xs text-muted-foreground mt-6 pt-6 border-t border-border leading-relaxed">
                      <span className="text-primary font-medium">Best suited to: </span>{plan.best_suited}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-20 flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="overline">Not sure which option is right for you?</div>
              <h3 className="font-serif-display text-3xl text-primary mt-3">Get a free, no-pressure valuation first.</h3>
            </div>
            <div className="flex gap-3">
              <Link to={tenantPath("valuation")}>
                <Button size="lg" className="rounded-sm sharp-btn">
                  {isUs ? "Home valuation" : "Free valuation"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
